import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
    const { login } = useAuth();
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const next = params.get("next") || "/dashboard";
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        try {
            setBusy(true);
            await login(email, password);
            toast.success("Welcome back");
            navigate(next);
        } catch (err) {
            toast.error("Login failed", { description: err?.response?.data?.detail || err.message });
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-[80vh] grid lg:grid-cols-2">
            <div className="hidden lg:block bg-klein text-white p-16 relative">
                <div className="grid-line opacity-20 absolute inset-0" />
                <div className="relative h-full flex flex-col justify-between">
                    <p className="overline text-white/70">ai4faculty.info</p>
                    <div>
                        <h2 className="font-heading font-black text-5xl tracking-tighter leading-[0.95]">
                            Sign in to access<br />your modules.
                        </h2>
                        <p className="mt-6 text-white/80 max-w-md">
                            Pick up where you left off. Templates, prompts, workflows — all yours.
                        </p>
                    </div>
                </div>
            </div>
            <div className="flex items-center justify-center px-6 py-16">
                <form onSubmit={submit} data-testid="login-form" className="w-full max-w-md">
                    <h1 className="font-heading font-black text-4xl tracking-tighter">Sign in</h1>
                    <p className="text-muted-foreground mt-2">Welcome back.</p>

                    <div className="mt-8 space-y-5">
                        <label className="flex flex-col gap-2">
                            <span className="text-xs uppercase tracking-widest font-semibold">Email</span>
                            <input
                                data-testid="login-email"
                                required
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="w-full bg-white border border-black/20 px-3 py-2.5 text-sm focus:outline-none focus:border-klein focus:ring-2 focus:ring-klein/20"
                            />
                        </label>
                        <label className="flex flex-col gap-2">
                            <span className="text-xs uppercase tracking-widest font-semibold">Password</span>
                            <input
                                data-testid="login-password"
                                required
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="w-full bg-white border border-black/20 px-3 py-2.5 text-sm focus:outline-none focus:border-klein focus:ring-2 focus:ring-klein/20"
                            />
                        </label>
                        <button
                            type="submit"
                            data-testid="login-submit"
                            disabled={busy}
                            className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-klein text-white text-sm font-semibold hover:bg-black transition-colors disabled:opacity-50"
                        >
                            {busy ? "Signing in..." : "Sign in"} <ArrowRight size={16} />
                        </button>
                    </div>

                    <p className="mt-6 text-sm text-muted-foreground">
                        New here?{" "}
                        <Link to={`/register${next ? `?next=${encodeURIComponent(next)}` : ""}`} className="text-klein font-semibold underline-offset-4 hover:underline" data-testid="goto-register">
                            Create an account
                        </Link>
                    </p>
                </form>
            </div>
        </div>
    );
}
